import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import api from '../../lib/api';
import { PRIVACY_POLICY_VERSION } from '../../lib/privacy-version';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (slug: string) => void;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function CreatePracticeModal({ open, onClose, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [confirmedIntraProfessional, setConfirmedIntraProfessional] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const slug = toSlug(name);
      const { data } = await api.post('/api/v1/orgs', { name: name.trim(), slug });
      // Re-affirm consent against the current policy version (defensive; server
      // already stored it at registration, but practice creation is a new
      // administrative act that deserves its own explicit ack).
      try {
        await api.post('/api/v1/users/consent', { version: PRIVACY_POLICY_VERSION });
      } catch {
        // Non-fatal — the org was created; consent reaffirmation is best-effort.
      }
      return data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      setName('');
      setError('');
      setAcceptedPrivacy(false);
      setAcceptedTos(false);
      setConfirmedIntraProfessional(false);
      onClose();
      onCreated(data.slug);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr.response?.data?.error?.message || 'Failed to create practice');
    },
  });

  const allChecked = acceptedPrivacy && acceptedTos && confirmedIntraProfessional;
  const canSubmit = name.trim().length >= 2 && allChecked && !mutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title="Create Practice">
      {error && <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-600">{error}</div>}

      <div className="mb-4 rounded-lg border-2 border-red-500/40 bg-red-50 p-3 text-xs leading-relaxed text-red-900 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">
        <strong className="mb-1 block">Intra-professional use only</strong>
        Each practice you create is a workspace for practitioner-to-practitioner communication.
        ClinikChat must not be used to communicate with patients, and patients must not be given
        accounts. By creating a practice, the acknowledgements below apply to every user you invite
        into it.
      </div>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Practice Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) mutation.mutate();
            }}
            className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="e.g. Sunrise Physio"
            autoFocus
          />
          {name.trim().length >= 2 && (
            <p className="mt-1 text-xs text-gray-400">URL: {toSlug(name)}</p>
          )}
        </div>

        <div className="space-y-2 pt-1">
          <label className="flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-gray-700">
            <input
              type="checkbox"
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              I have read and accept the{' '}
              <Link to="/privacy" target="_blank" className="text-primary underline">
                Privacy Policy
              </Link>{' '}
              (version {PRIVACY_POLICY_VERSION}) on behalf of this practice.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-gray-700">
            <input
              type="checkbox"
              checked={acceptedTos}
              onChange={(e) => setAcceptedTos(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              I have read and accept the{' '}
              <Link to="/terms" target="_blank" className="text-primary underline">
                Terms of Service
              </Link>
              .
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-gray-700">
            <input
              type="checkbox"
              checked={confirmedIntraProfessional}
              onChange={(e) => setConfirmedIntraProfessional(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              I confirm this practice will use ClinikChat <strong>strictly</strong> for
              intra-professional communication, and will not be used to communicate with patients or
              issue patients ClinikChat accounts.
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating...' : 'Create Practice'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
